// Cascading auto-fill on the new-client form. Operator types the
// display name first; everything downstream derives:
//
//   display name  →  slug          (lowercase, dashed)
//                 →  dataset_name  ("<slug with _>_predict" — BQ rejects dashes)
//                 →  state_bucket  ("<slug>-predict-state")
//
// Each derived field stops auto-updating once it's manually edited,
// so the operator can override anywhere in the chain. Clearing a
// manually-edited field re-arms its auto-derive.
//
// Mirrors ClientsController#create's server-side `||=` fallbacks; this
// controller is purely for live UX. With JS disabled the operator
// types fields manually and the form still works.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["displayName", "slug", "datasetName", "stateBucket"]

  connect() {
    // Pre-filled values (edit form, partial typing before JS booted)
    // are treated as manual — we never clobber.
    this.slugUserEdited        = this.slugTarget.value.length > 0
    this.datasetNameUserEdited = this.datasetNameTarget.value.length > 0
    this.stateBucketUserEdited = this.stateBucketTarget.value.length > 0
  }

  // Display name → slug → cascade
  syncFromDisplayName() {
    if (!this.slugUserEdited) {
      this.slugTarget.value = this.slugify(this.displayNameTarget.value)
    }
    this.cascadeSlugDerivatives()
  }

  // Operator typed directly into the slug field — note as edited and
  // re-cascade dataset/bucket.
  markSlugDirty() {
    this.slugUserEdited = this.slugTarget.value.length > 0
    this.cascadeSlugDerivatives()
  }

  markDatasetNameDirty() {
    this.datasetNameUserEdited = this.datasetNameTarget.value.length > 0
  }

  markStateBucketDirty() {
    this.stateBucketUserEdited = this.stateBucketTarget.value.length > 0
  }

  // ---- internals --------------------------------------------------

  cascadeSlugDerivatives() {
    const slug = this.slugTarget.value.trim()
    if (!this.datasetNameUserEdited) {
      // BQ dataset names reject hyphens — underscore the slug part.
      const datasetSlug = slug.replace(/-/g, "_")
      this.datasetNameTarget.value = slug ? `${datasetSlug}_predict` : ""
    }
    if (!this.stateBucketUserEdited) {
      this.stateBucketTarget.value = slug ? `${slug}-predict-state` : ""
    }
  }

  // "Acme Corp." → "acme-corp", "Peddle" → "peddle", "  X  " → "x"
  slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")  // anything-not-alnum → dash
      .replace(/^-+|-+$/g, "")      // trim leading/trailing dashes
  }
}
